/**
 * Role-Based Access Control (RBAC) permissions config.
 *
 * Menu visibility:
 *   visibleMenuIds – list of sidebar section ids the role can see.
 *   '*' means all sections.
 *
 * Actions:
 *   canAdd / canEdit / canDelete – global defaults per role.
 *   overrides – per-section overrides keyed by section id.
 *
 * Override extra flags:
 *   viewOnly       – alias for canAdd/canEdit/canDelete = false.
 *   hidePrices     – hides purchase_rate & sale_rate columns.
 *   hiddenSubItems – array of sub-item ids to hide entirely for this role.
 *   ticketsOnly    – (legacy) limits Services Hub to Tickets only.
 */

const ROLE_PERMISSIONS = {
  Superadmin: {
    visibleMenuIds: '*',
    canAdd: true,
    canEdit: true,
    canDelete: true,
    overrides: {},
  },
  Admin: {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability', 'org-data',
      'master-data', 'config-settings', 'inward', 'inventory',
      'internal-transfer', 'part-sale-tracker', 'purchase', 'sale',
      'transactions', 'services',
    ],
    canAdd: true,
    canEdit: false,
    canDelete: false,
    overrides: {},
  },

  // ── Accountant ──────────────────────────────────────────────────────────────
  // Based on the Accountant permissions matrix.
  // Org Data is hidden (all No). Lead Source + Receivables hidden within Services Hub.
  // Inward DC = same CRUD as Delivery Challan (full access).
  Accounts: {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'master-data',           // View/Add/Edit/Delete = Yes
      'config-settings',       // View = Yes, rest = No
      'inward',                // View = Yes, rest = No
      'inventory',             // View = Yes (NA for rest)
      'internal-transfer',     // View/Add/Edit/Delete = Yes
      'part-sale-tracker',     // View = Yes (NA for rest)
      'purchase',              // sub-items have individual overrides
      'sale',                  // all sub-items = full CRUD
      'transactions',          // View = Yes (NA for rest)
      'services',              // Leads = view, Tickets = view, Lead Source + Receivables HIDDEN
      // 'org-data' intentionally omitted → hidden from menu
    ],
    canAdd: true,    // global default (Purchase + Sale + Master Data = full CRUD)
    canEdit: true,
    canDelete: true,
    overrides: {
      // View-only sections
      'inward':            { canAdd: false, canEdit: false, canDelete: false },
      'config-settings':   { canAdd: false, canEdit: false, canDelete: false },
      'inventory':         { canAdd: false, canEdit: false, canDelete: false },
      'part-sale-tracker': { canAdd: false, canEdit: false, canDelete: false },
      'transactions':      { canAdd: false, canEdit: false, canDelete: false },
      // Services Hub: view-only for Leads/Tickets, completely hide Lead Source + Receivables
      'services':          { canAdd: false, canEdit: false, canDelete: false, hiddenSubItems: ['lead-sources', 'receivables', 'requirements', 'service-steps', 'service-tasks'] },
    },
  },

  // ── Inventory Manager ──────────────────────────────────────────────────────
  // Full matrix from the Inventory Manager permissions table.
  // Settings + Internal Transfer → hidden (all No).
  // Payment In + Lead Source + Receivables → hidden sub-items.
  // Inward DC = Full CRUD. Master Data + Inward = Full CRUD.
  // Purchase sub-items mostly view-only; Debit Notes = view+add.
  // Sale sub-items: Invoices/DC/DC-Return/Sale-Return = view+add; rest view-only.
  'Inventory Manager': {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'org-data',          // View=Yes, rest=No
      'master-data',       // Full CRUD
      // 'config-settings' intentionally omitted → hidden (all No)
      'inward',            // Full CRUD
      'inventory',         // View only (NA)
      // 'internal-transfer' intentionally omitted → hidden (all No)
      'part-sale-tracker', // View only (NA)
      'purchase',          // mixed per sub-item
      'sale',              // mixed; payment-in hidden
      'transactions',      // View only (NA)
      'services',          // Leads+Tickets=view; Lead Source+Receivables HIDDEN
    ],
    canAdd: false,   // most conservative global; overrides add access where needed
    canEdit: false,
    canDelete: false,
    overrides: {
      // Full CRUD sections
      'master-data':  { canAdd: true, canEdit: true, canDelete: true },
      'inward':       { canAdd: true, canEdit: true, canDelete: true },
      // Purchase sub-items
      'purchase-return':  { canAdd: true,  canEdit: false, canDelete: false }, // Debit Notes
      'inward-challan':   { canAdd: true,  canEdit: true,  canDelete: true  }, // Inward DC
      // Sale sub-items
      'sale-invoices':    { canAdd: true,  canEdit: false, canDelete: false },
      'delivery-challan': { canAdd: true,  canEdit: false, canDelete: false },
      'sale-return':      { canAdd: true,  canEdit: false, canDelete: false }, // Credit Note
      // Section-level hidden sub-items
      'sale':     { canAdd: false, canEdit: false, canDelete: false, hiddenSubItems: ['payment-in'] },
      'services': { canAdd: false, canEdit: false, canDelete: false, hiddenSubItems: ['lead-sources', 'receivables', 'requirements', 'service-steps', 'service-tasks'] },
    },
  },

  // ── Service Manager ────────────────────────────────────────────────────────
  // Purchase, Sale, Transactions, Org Data, Master Data, Settings,
  // Inward, Internal Transfer → hidden (all No).
  // Inventory + Part Sale Tracker → view only.
  // Services Hub → Full CRUD for Leads, Lead Source, Receivables; Tickets = view only (NA).
  'Service Manager': {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'inventory',
      'part-sale-tracker',
      'services',
    ],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      // Each Services Hub sub-item now has its own section ID in PATH_TO_SECTION
      'leads':         { canAdd: true, canEdit: true, canDelete: true },
      'lead-sources':  { canAdd: true, canEdit: true, canDelete: true },
      'receivables':   { canAdd: true, canEdit: true, canDelete: true },
      'requirements':  { canAdd: true, canEdit: true, canDelete: true },
      'service-steps': { canAdd: true, canEdit: true, canDelete: true },
      'service-tasks': { canAdd: true, canEdit: true, canDelete: true },
      // tickets: no override → global (canAdd:false) = view only ✓
    },
  },

  // ── Receptionist ───────────────────────────────────────────────────────────
  // Org Data, Master Data, Settings, Inward, Internal Transfer → hidden.
  // Payment Out hidden under Purchase.
  // Inventory + Part Sale Tracker + all Purchase + all Sale + Transactions = view only.
  // Services Hub: Leads / Lead Source / Receivables = Full CRUD; Tickets = view only (NA).
  // ── Admin-HR ────────────────────────────────────────────────────────────────
  // Org Data = Full CRUD. Master Data + Settings + Internal Transfer + Services Hub → hidden.
  // Inward + Inventory + Part Sale Tracker + all Purchase + Transactions = view only.
  // Sale: Delivery Challan + DC Return = view+add; everything else view only.
  'Admin-HR': {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'org-data',          // Full CRUD
      // 'master-data' → hidden
      // 'config-settings' → hidden (Settings)
      'inward',            // view only
      'inventory',         // view only (NA)
      // 'internal-transfer' → hidden
      'part-sale-tracker', // view only (NA)
      'purchase',          // all sub-items view only
      'sale',              // DC + DC Return = view+add; rest view only
      'transactions',      // view only (NA)
      // 'services' → hidden (all sub-items No)
    ],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      'org-data':          { canAdd: true, canEdit: true, canDelete: true },
      'delivery-challan':  { canAdd: true, canEdit: false, canDelete: false },
      // delivery-challan-return will inherit the same when implemented
    },
  },

  Receptionist: {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'inventory',         // view only
      'part-sale-tracker', // view only
      'purchase',          // all sub-items view only; Payment Out hidden
      'sale',              // all sub-items view only
      'transactions',      // view only
      'services',          // mixed (see overrides)
      // org-data, master-data, config-settings, inward, internal-transfer → hidden
    ],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      // Hide Payment Out within Purchase
      'purchase': { canAdd: false, canEdit: false, canDelete: false, hiddenSubItems: ['payment-out'] },
      // Services Hub per-sub-item CRUD
      'leads':         { canAdd: true, canEdit: true, canDelete: true },
      'lead-sources':  { canAdd: true, canEdit: true, canDelete: true },
      'receivables':   { canAdd: true, canEdit: true, canDelete: true },
      'requirements':  { canAdd: true, canEdit: true, canDelete: true },
      'service-steps': { canAdd: true, canEdit: true, canDelete: true },
      'service-tasks': { canAdd: true, canEdit: true, canDelete: true },
      // tickets: no override → global (view only) ✓
    },
  },

  // ── Delivery Person ─────────────────────────────────────────────────────────
  // Org Data, Master Data, Settings, Inward, Internal Transfer,
  // Part Sale Tracker, Transactions → hidden.
  // Purchase visible: Bills + Payment Out + Orders hidden; Return/DC/DC Return = view only.
  // Sale visible: Estimates + Proforma + Payment In hidden; Invoices + Sale Return = view only;
  // Delivery Challan + DC Return = view+add.
  // Services Hub: all 4 sub-items visible but view only.
  'Delivery Person': {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'inventory',    // view only (NA)
      'purchase',     // partial — Bills/Payment Out/Orders hidden
      'sale',         // partial — Estimates/Proforma/Payment In hidden
      'services',     // all sub-items view only
    ],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      // Purchase: hide Bills, Payment Out, Orders
      'purchase': { canAdd: false, canEdit: false, canDelete: false, hiddenSubItems: ['purchase-bills', 'payment-out', 'purchase-orders'] },
      // Sale: hide Estimates, Proforma Invoice, Payment In; DC = view+add
      'sale':     { canAdd: false, canEdit: false, canDelete: false, hiddenSubItems: ['estimates', 'proforma-invoice', 'payment-in'] },
      'delivery-challan': { canAdd: true, canEdit: false, canDelete: false },
      // services sub-items: global (view only) ✓
    },
  },

  // ── Sales Manager ──────────────────────────────────────────────────────────
  // Org Data, Master Data, Settings, Internal Transfer, Purchase (all), Transactions → hidden.
  // Payment In + Sale Return hidden sub-items under Sale.
  // Inward + Inventory + Part Sale Tracker + DC + DC Return + Tickets + Lead Source + Receivables = view only.
  // Sale Invoices + Estimates + Proforma = view+add. Leads = Full CRUD.
  'Sales Manager': {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'inward',            // view only
      'inventory',         // view only (NA)
      'part-sale-tracker', // view only (NA)
      // 'purchase' → hidden (all No)
      'sale',              // mixed; payment-in + sale-return hidden
      // 'transactions' → hidden (No)
      'services',          // Leads=CRUD; Tickets/Lead Source/Receivables=view
    ],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      // Sale sub-items with add access
      'sale-invoices': { canAdd: true, canEdit: false, canDelete: false },
      'estimates':     { canAdd: true, canEdit: false, canDelete: false },
      'proforma':      { canAdd: true, canEdit: false, canDelete: false },
      // Hide Payment In + Sale Return within Sale section
      'sale': { canAdd: false, canEdit: false, canDelete: false, hiddenSubItems: ['payment-in', 'sale-return'] },
      // Services Hub
      'leads': { canAdd: true, canEdit: true, canDelete: true },
      // tickets, lead-sources, receivables → global (view only) ✓
    },
  },
  // ── Service Technician ─────────────────────────────────────────────────────
  // Same as Junior Technician table.
  // Org Data, Master Data, Settings, Inward, Internal Transfer,
  // Part Sale Tracker, Purchase (all), Sale (all), Transactions → hidden.
  // Inventory → view only. Services Hub → all sub-items visible;
  // Leads = Full CRUD; Tickets/Lead Source/Receivables = view only.
  'Service Technician': {
    visibleMenuIds: [
      'dashboard', 'search-item', 'stock-availability',
      'inventory',  // view only
      'services',   // all sub-items visible
      // Part Sale Tracker: View=No → hidden
    ],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      'leads': { canAdd: true, canEdit: true, canDelete: true },
      // tickets, lead-sources, receivables: global = view only ✓
    },
  },
};

// Default for unknown roles – read-only everything
const DEFAULT_PERMS = {
  visibleMenuIds: ['dashboard'],
  canAdd: false,
  canEdit: false,
  canDelete: false,
  overrides: {},
};

export function getUserRole() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    const user = JSON.parse(raw);
    return user.role || '';
  } catch { return ''; }
}

export function getPermissions(role) {
  if (!role) role = getUserRole();

  // 1. Static predefined roles take priority
  if (ROLE_PERMISSIONS[role]) return ROLE_PERMISSIONS[role];

  // 2. Dynamic custom role — read permissions_matrix from localStorage
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const user = JSON.parse(raw);
      if (user.role === role && user.role_permissions) {
        return user.role_permissions;
      }
    }
  } catch { /* ignore */ }

  return DEFAULT_PERMS;
}

/** Check if a menu section id is visible for the current role */
export function isMenuVisible(sectionId, role) {
  const perms = getPermissions(role);
  if (perms.visibleMenuIds === '*') return true;
  return perms.visibleMenuIds.includes(sectionId);
}

/** Check a nested menu item, including visibility inherited from its parent. */
export function isSubItemVisible(parentId, subItemId, role) {
  if (!isMenuVisible(parentId, role)) return false;
  return !getSectionPerms(parentId, role).hiddenSubItems.includes(subItemId);
}

/** Get action permissions for a specific section */
export function getSectionPerms(sectionId, role) {
  const perms = getPermissions(role);
  const legacySectionId = sectionId === 'lead-sources' ? 'service-items' : null;
  const ov = perms.overrides[sectionId] || (legacySectionId ? perms.overrides[legacySectionId] : null) || {};
  const hiddenSubItems = [...(ov.hiddenSubItems || [])];
  if (hiddenSubItems.includes('item-list') && !hiddenSubItems.includes('lead-sources')) {
    hiddenSubItems.push('lead-sources');
  }
  return {
    canAdd: ov.canAdd !== undefined ? ov.canAdd : perms.canAdd,
    canEdit: ov.canEdit !== undefined ? ov.canEdit : perms.canEdit,
    canDelete: ov.canDelete !== undefined ? ov.canDelete : perms.canDelete,
    viewOnly: ov.viewOnly || false,
    hidePrices: ov.hidePrices || false,
    ticketsOnly: false,
    hiddenSubItems,
  };
}

/**
 * Map URL path segments to section IDs for auto-detection.
 * Each Purchase and Sale sub-item gets its own section ID so roles
 * can apply per-sub-item overrides (e.g. Inventory Manager).
 * Roles that don't override a specific sub-item ID fall back to
 * their global canAdd/canEdit/canDelete defaults automatically.
 */
const PATH_TO_SECTION = {
  'master-data': 'master-data',
  companies: 'master-data', branches: 'master-data', brands: 'master-data',
  categories: 'master-data', 'sub-categories': 'master-data', components: 'master-data',
  products: 'master-data', racks: 'master-data', specs: 'master-data',
  taxes: 'master-data', units: 'master-data', vendors: 'master-data',
  customers: 'master-data', 'payment-terms': 'master-data', 'payment-modes': 'master-data',
  warranty: 'master-data',

  // Purchase sub-items — each mapped to its own section ID
  purchase:          'purchase',
  bills:             'purchase-bills',
  'payment-out':     'payment-out',
  orders:            'purchase-orders',
  'debit-notes':     'purchase-return',
  'inward-challan':  'inward-challan',
  'return-inward-challan': 'return-inward-challan',

  // Sale sub-items — each mapped to its own section ID
  sale:              'sale',
  invoices:          'sale-invoices',
  estimates:         'estimates',
  proforma:          'proforma',
  'payment-in':      'payment-in',
  'delivery-challan': 'delivery-challan',
  'delivery-challans': 'delivery-challan',
  'credit-notes':    'sale-return',

  inward:            'inward',
  inventory:         'inventory',
  'internal-transfers': 'internal-transfer',
  'part-sale':       'part-sale-tracker',
  'part-sale-tracker': 'part-sale-tracker',

  services: 'services', leads: 'leads', 'leads-data': 'leads-data', tickets: 'tickets',
  tasks: 'services', 'task-master': 'service-tasks', 'step-master': 'service-steps', 'lead-sources': 'lead-sources', receivables: 'receivables', requirements: 'requirements',

  transactions: 'transactions',
  'org-data': 'org-data', users: 'org-data', roles: 'org-data',
  hr: 'hr', attendance: 'attendance', leaves: 'leaves',
  settings: 'config-settings',
  'invoice-settings': 'config-settings',
  'data-management': 'config-settings',
  'config-settings': 'config-settings',
};

/** Auto-detect section from current window path and return permissions */
export function detectPerms() {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  // Walk from the end to find the most specific match
  for (let i = segments.length - 1; i >= 0; i--) {
    const sectionId = PATH_TO_SECTION[segments[i]];
    if (sectionId) return getSectionPerms(sectionId);
  }
  return getSectionPerms('dashboard');
}
